<template>
  <div
    class="editor"
    ref="editorMain"
    :style="`height: ${editorHeight}`"
    :class="{
      'is-invalid':
        (validator && validator.$model && validator.$invalid) || (validator && validator.$error),
      'is-valid': validator && validator.$model && !validator.$invalid
    }"
    v-shortkey="lowerCaseShortkey"
    @shortkey="toggleLowerCase"
  >
    <DxHtmlEditor
      @value-changed="handleValueChange"
      :value="editorOutput"
      value-type="html"
      ref="editor"
      :toolbar="toolbar"
      @focusIn="handleFocusIn"
      @focusOut="handleFocusOut"
      :allowSoftLineBreak="true"
      :customize-modules="customizeModules"
      :element-attr="elementAttr"
      @initialized="onInitialized"
      @content-ready="initializeEditor"
      :mentions="mentions"
    >
    </DxHtmlEditor>

    <div class="m-1 validation-container">
      <template v-if="(validator && validator.$error) || (validator && validator.$invalid)">
        <div
          class="validation-error"
          v-for="(key, index) in Object.keys(validator.$params)"
          :key="index"
        >
          <span class="error" v-if="!validator[key]">
            {{ validatorMsgMap[key] }}
          </span>
        </div>
      </template>
    </div>
  </div>
</template>

<script>
import { scrollToElement, validatorMsgMapBase } from "../../modules/helpers";
import { DxHtmlEditor } from "devextreme-vue/html-editor";
import { mapState } from "vuex";
import CustomClipboard from "@/modules/Quill/Clipboard";
import { isEmpty } from "lodash";
import { fontSizes, fontWhiteList } from "@/modules/enums";
import Quill from "devextreme-quill";

import { capitalize, altKey } from "../../modules/helpers";
import { getDefaultStyles } from "@/modules/getDefaultStylesByField";

/**
 *
 * @param {Object} settings
 * @param {Object} settings.labSettings
 * @param {Object} settings.state
 */
function initializeQuill() {
  const Block = Quill.import("blots/block");
  const Inline = Quill.import("blots/inline");
  const Italics = Quill.import("formats/italic");
  const Parchment = Quill.import("parchment");
  const config = {
    scope: Parchment.Scope.INLINE,
    whitelist: ["uppercase", "lowercase"]
  };
  const TransformClass = new Parchment.ClassAttributor("transform", "ql-transformed", config);
  class TransformStyleAttributor extends Parchment.StyleAttributor {
    value(node) {
      return super.value(node).replace(/["']/g, "");
    }
  }
  const TransformStyle = new TransformStyleAttributor("transform", "text-transform", config);

  Block.tagName = "DIV"; //Quill uses <p> by default
  Italics.tagName = "I";

  class CustomBold extends Inline {
    static create(value) {
      let node = super.create(value);

      node.style.fontWeight = "bold";
      return node;
    }
    static formats(node) {
      return node.style.fontWeight;
    }
  }
  CustomBold.tagName = "span"; // Quill uses <strong> by default
  CustomBold.blotName = "bold";
  Quill.register(
    {
      "attributors/class/transform": TransformClass,
      "attributors/style/transform": TransformStyle
    },
    true
  );

  Quill.register(
    {
      "blots/block": Block,
      "blots/inline": CustomBold,
      "formats/italic": Italics,
      "modules/clipboard": CustomClipboard,
      "formats/transform": TransformStyle
    },
    true
  );
}
initializeQuill();

export default {
  props: {
    height: Number,
    name: {
      type: String,
      required: true
    },
    label: {
      type: String
    },
    value: { required: true },
    upperCase: {
      type: Boolean,
      default: false
    },
    validator: {
      type: Object
    },
    validatorMsgMap: {
      type: Object,
      default() {
        return validatorMsgMapBase;
      }
    },
    accessKey: String,
    mentions: {
      type: Array,
      default() {
        return [];
      }
    }
  },
  components: {
    DxHtmlEditor
  },
  data: () => ({
    isToolbarExpanded: false,
    targetList: [],
    proofReaderOpen: false,
    sizeValues: fontSizes,
    fontValues: fontWhiteList,
    component: {},
    webSpellCheckInstance: null,
    isFocused: false,
    headerValues: [false, 1, 2, 3, 4, 5],
    isMultiline: true,
    elementAttr: {
      class: "quill_editor"
    },
    _editor: null,
    resizeObserver: null,
    willReopen: false,
    preventExpand: false,
    lastSelection: null,
    lowerCaseShortkey: altKey("z"),
    activeChecks: 0
  }),
  methods: {
    onInitialized({ component }) {
      this.$emit("initialized", { component });
    },
    customizeModules(config) {
      config.clipboard = {
        matchVisual: false
      };
      if (this.labSettings.EditorToolbarDisableBulletPoints) {
        config.keyboard.bindings = { ...config.keyboard.bindings, "list autofill": null };
      }
      this.$emit("customize-modules", config);
    },
    displayName(key = "", name) {
      if (key) {
        const regex = new RegExp(key, "i");
        if (regex.test(name)) {
          const { index } = name.match(regex);
          return `${name.slice(0, index)}<u>${name[index]}</u>${name.slice(index + 1)}`;
        }
      }
      return `${name || ""}`;
    },

    async focus(isManual) {
      this.component.focus();
      scrollToElement(this.$el);
      if (isManual) {
        this.handleFocusIn(true);
      }
    },
    handleValueChange(e) {
      this.editorOutput = e.value;
    },
    applyDefaultFormat() {
      if (this._editor.getSelection()) {
        for (const key in this.styles) {
          this._editor.format(key, this.styles[key]);
        }
      }
    },
    initializeEditor({ component }) {
      this._editor = component.getQuillInstance();
      this.component = component;
      const keyboard = component.getModule("keyboard");
      keyboard.addBinding(
        {
          key: 74,
          altKey: true
        },
        this.jumpToTilde
      );
      component.getModule("keyboard").bindings.tab.unshift({
        key: 9,
        handler: function (range) {
          this.quill.insertText(range.index, "\xA0\xA0\xA0\xA0");
          return false;
        }
      });
      this.applyDefaultFormat();

      // function checkNextTextEvent(engine, eventName) {
      //   return new Promise(resolve => {
      //     engine.once(eventName, resolve);
      //   });
      // }

      this._editor.on("editor-change", eventName => {
        if (eventName === "selection-change" && this.isFocused) {
          this.checkFormatting();

          // // This was not working in certan circumstances. May need to be brough back.
          // // eslint-disable-next-line no-unused-vars
          // const [newRange, oldRange, source] = args;
          // if (newRange && newRange?.index === 0) {
          //   this.checkFormatting();
          // }
          // if (source === "silent") {
          //   checkNextTextEvent(this._editor, "text-change").then((...args) => {
          //     const [delta] = args;
          //     if (delta?.ops instanceof Array) {
          //       const lastInsert = delta.ops[delta.ops.length - 1];
          //       if (lastInsert?.delete === 1) {
          //         this.checkFormatting();
          //       }
          //     }
          //   });
          // }
        }
      });
      this.$emit("content-ready", { component });
    },
    jumpToTilde(range) {
      const text = this._editor.getText();
      if (!text.includes("~")) {
        return true;
      }
      const matches = text.matchAll(/[~]/g);
      for (const match of matches) {
        if (match.index > range.index) {
          return this._editor.setSelection(match.index, 1, "silent");
        }
      }
      const firstTilde = text.indexOf("~");
      if (firstTilde > -1) {
        return this._editor.setSelection(firstTilde, 1, "silent");
      }
    },
    _getCharByIndex(index) {
      return this.editor.getContents(index, 1).ops[0].insert;
    },
    handleFocusIn(isManual) {
      if (this.isFocused && !isManual) {
        return;
      }
      if (this.lastSelection) {
        this._editor.setSelection(this.lastSelection.index, this.lastSelection.length, "silent");
      } else {
        const selection = this.component.getSelection();
        //Check if the selection is null on the editor.
        if (selection === null || selection?.length === 0) {
          //Grab the inner quill instance.
          const quill = this.component.getQuillInstance();
          const text = this.component.getText(0);
          const length = quill.getLength();
          /**
           * Most times the -2 was working because the editor would have \n\n at the end.
           */
          const newLineChar = /\n/i;
          let trailingNewLines = 0;
          for (let i = length - 1; i > 0; i--) {
            const char = text[i];
            if (newLineChar.test(char)) {
              trailingNewLines++;
            } else {
              break;
            }
          }
          this.component.setSelection(length ? length - trailingNewLines : 0, 0);
        }
      }
      const format = this.component.getFormat();
      if (isEmpty(format)) {
        this.applyDefaultFormat();
      }
      this.isFocused = true;
    },
    handleFocusOut() {
      const selection = this.component.getSelection();
      this.lastSelection = selection;
      this.isFocused = false;
    },
    handleMacroTrigger(range, context, editor, name) {
      this.checkMacroPhrase({
        range,
        context,
        editor,
        name,
        component: this.component
      });
    },
    checkFormatting() {
      const lineFormat = this._editor.getFormat();
      if (!lineFormat || isEmpty(lineFormat)) {
        this.applyDefaultFormat();
      }
    },
    toggleLowerCase() {
      if (!this.isForcedUpperCase) {
        return;
      }
      const range = this._editor.getSelection();
      const formats = this._editor.getFormat(range);
      if (formats.transform === "lowercase") {
        this._editor.format("transform", null);
      } else {
        this._editor.format("transform", "lowercase");
      }
    },
    setSpellCheckStatus(event) {
      if (event.name === this.name) {
        event.status ? this.activeChecks++ : this.activeChecks--;
      }
      if (this.isFocused) {
        setTimeout(
          () => {
            this.$emit("toggleBaseSpellChecker", this.activeChecks > 0);
          },
          this.activeChecks > 0 ? 0 : 500
        );
      }
    }
  },
  computed: {
    ...mapState({
      labSettings: state => state.labSettings,
      textEditors: state => state.applicationSettings.textEditors,
      dictionaryName: state => state.applicationSettings.dictionaryName,
      activeSpellCheckers: state => state.sessionDetails.activeSpellCheckers
    }),
    editorHeight() {
      if (this.height) {
        if (this.height > 150) {
          return this.height + "px";
        }
        return "150px";
      }
      const savedEditor = this.textEditors[this.name];
      if (savedEditor && savedEditor?.height) {
        return savedEditor.height + "px";
      }
      return "320px";
    },
    styles() {
      return getDefaultStyles(this.name, true);
    },
    toolbar() {
      if (this.toolbarDisabled) {
        return [];
      }
      return {
        multiline: true,
        items: [
          {
            name: "undo",
            locateInMenu: "auto",
            showText: "inMenu",
            options: {
              tabIndex: -1
            }
          },
          {
            name: "redo",
            locateInMenu: "auto",
            showText: "inMenu",
            options: {
              tabIndex: -1
            }
          },
          {
            name: "separator",
            locateInMenu: "auto",
            showText: "inMenu",
            options: {
              tabIndex: -1
            }
          },
          {
            name: "size",
            locateInMenu: "auto",
            showText: "inMenu",
            acceptedValues: this.sizeValues,
            options: {
              tabIndex: -1
            }
          },
          {
            name: "font",
            locateInMenu: "auto",
            showText: "inMenu",
            acceptedValues: this.fontValues,
            options: {
              tabIndex: -1
            }
          },
          {
            name: "bold",
            locateInMenu: "auto",
            showText: "inMenu",
            options: {
              tabIndex: -1
            }
          },
          {
            name: "italic",
            locateInMenu: "auto",
            showText: "inMenu",
            options: {
              tabIndex: -1
            }
          },
          {
            name: "strike",
            locateInMenu: "auto",
            showText: "inMenu",
            options: {
              tabIndex: -1
            }
          },
          {
            name: "underline",
            locateInMenu: "auto",
            showText: "inMenu",
            options: {
              tabIndex: -1
            }
          },
          {
            name: "separator",
            locateInMenu: "auto",
            showText: "inMenu",
            options: {
              tabIndex: -1
            }
          },
          {
            name: "alignLeft",
            locateInMenu: "auto",
            showText: "inMenu",
            options: {
              tabIndex: -1
            }
          },
          {
            name: "alignCenter",
            locateInMenu: "auto",
            showText: "inMenu",
            options: {
              tabIndex: -1
            }
          },
          {
            name: "alignRight",
            locateInMenu: "auto",
            showText: "inMenu",
            options: {
              tabIndex: -1
            }
          },
          {
            name: "separator",
            locateInMenu: "auto",
            showText: "inMenu",
            options: {
              tabIndex: -1
            }
          },
          {
            name: "orderedList",
            locateInMenu: "auto",
            showText: "inMenu",
            options: {
              tabIndex: -1
            },
            visible: !this.labSettings.EditorToolbarDisableBulletPoints
          },
          {
            name: "bulletList",
            locateInMenu: "auto",
            showText: "inMenu",
            options: {
              tabIndex: -1
            },
            visible: !this.labSettings.EditorToolbarDisableBulletPoints
          },
          {
            name: "header",
            locateInMenu: "auto",
            showText: "inMenu",
            acceptedValues: this.headerValues,
            options: {
              tabIndex: -1
            }
          },
          {
            name: "color",
            locateInMenu: "auto",
            showText: "inMenu",
            options: {
              tabIndex: -1
            }
          },
          {
            name: "separator",
            locateInMenu: "auto",
            showText: "inMenu",
            options: {
              tabIndex: -1
            }
          },
          {
            name: "link",
            locateInMenu: "auto",
            showText: "inMenu",
            options: {
              tabIndex: -1
            }
          },
          {
            name: "image",
            locateInMenu: "auto",
            showText: "inMenu",
            options: {
              tabIndex: -1
            }
          },
          {
            name: "transform",
            visible: this.isForcedUpperCase,
            locateInMenu: "auto",
            showText: "inMenu",
            widget: "dxButton",
            options: {
              tabIndex: -1,
              text: "Lowercase",
              hint: "Force Lowercase",
              icon: "shrinkfont",
              onClick: () => {
                this.toggleLowerCase();
              }
            }
          }
        ]
      };
    },
    editorOutput: {
      get() {
        return this.value;
      },
      set(value) {
        return this.$emit("input", value);
      }
    },
    editorRef() {
      return this.$refs.editor.instance;
    },
    toolbarDisabled() {
      if (
        this.labSettings.EditorToolbarsDisabled &&
        this.labSettings.EditorToolbarsDisabled.split(",").includes(this.name)
      ) {
        return true;
      }
      return false;
    },
    isForcedUpperCase() {
      let settingName = "";
      switch (this.name) {
        case "notes":
          settingName = "SpecimenNote";
          break;
        case "caseNotes":
          settingName = "CaseNote";
          break;
        default:
          settingName = capitalize(this.name);
      }
      if (settingName) {
        return Boolean(this.labSettings["ForceUpperCase" + settingName]);
      } else {
        return false;
      }
    }
  }
};
</script>
<style lang="scss" scoped>
.toolbar {
  width: 100%;
}
.title {
  text-transform: capitalize;
  font-size: 1.2rem;
}
.is-invalid {
  border-color: #dc3545 !important;
}
.is-valid {
  border-color: #28a745 !important;
}
.editor {
  resize: vertical;
  overflow: auto;
  display: flex;
  flex-direction: column;
  min-height: 150px;
  max-height: 75vh;
}
::v-deep .quill_editor,
.dx-htmleditor-toolbar-wrapper {
  flex: 1 0 auto;
}

::v-deep .dx-icon,
::v-deep .dx-dropdowneditor-icon {
  &::before {
    color: #333 !important;
  }
}
.ql-editor {
  padding: 12px 0;
  font-size: 18px;
}
</style>
